import React from 'react';
import { FormGroup, Label, Input, Button, Spinner, FormText, Row, Col } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import {
  useDetailQuery,
  useCreateMutation,
  useUpdateMutation,
} from 'helpers/http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'helpers/http-client/httpConstants';
import Select from 'react-select';
import { customSelectStyles } from 'components/Common/SelectStyle';
import * as Yup from 'yup';
import { Formik, Form, ErrorMessage } from 'formik';

export enum MedicineType {
  TabletPack = 'TabletPack',
  Syrup = 'Syrup',
  Injection = 'Injection',
  Capsule = 'Capsule',
  Drops = 'Drops',
  Ointment = 'Ointment',
  Cream = 'Cream',
  Powder = 'Powder',
  Inhaler = 'Inhaler',
  Lotion = 'Lotion',
  Gel = 'Gel',
  Suppository = 'Suppository',
  Patch = 'Patch',
  Solution = 'Solution',
  Spray = 'Spray',
  Suspension = 'Suspension',
  Other = 'Other',
}

export enum ApprovalStatus {
  Approved = 'Approved',
  Pending = 'Pending',
  Rejected = 'Rejected',
}

interface MedicineFormProps {
  medicineId?: string;
  onSuccess: (medicine: any) => void;
}

interface MedicineFormValues {
  id?: string;
  name: string;
  alias: string;
  genericName: string | null;
  description: string;
  dosageForm: string;
  strength: string;
  routeOfAdministration: string;
  medicineType: string;
  prescriptionRequired: boolean;
  storageConditions: string;
  shelfLifeMonths: number | null;
  approvalStatus: ApprovalStatus | string;
  approvalDate: string | null;
  isActive: boolean;
  itemAlert: boolean;
  allowDue: boolean;
  printable: boolean;
  generatePO: boolean;
  maxSaleDiscount: number | null;
  class: string | null;
}

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  alias: Yup.string().required('Alias is required'),
  genericName: Yup.string().nullable(),
  description: Yup.string().required('Description is required'),
  dosageForm: Yup.string().required('Dosage form is required'),
  strength: Yup.string().required('Strength is required'),
  routeOfAdministration: Yup.string().required('Route of Administration is required'),
  medicineType: Yup.string().required('Medicine Type is required'),
  prescriptionRequired: Yup.boolean().required(),
  storageConditions: Yup.string().required('Storage conditions are required'),
  shelfLifeMonths: Yup.number().nullable().min(0, 'Shelf life cannot be negative'),
  approvalStatus: Yup.string().required('Approval status is required'),
  approvalDate: Yup.string()
    .nullable()
    .matches(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/, 'Must be valid ISO date')
    .nullable(),
  isActive: Yup.boolean().required(),
  itemAlert: Yup.boolean().required(),
  allowDue: Yup.boolean().required(),
  printable: Yup.boolean().required(),
  generatePO: Yup.boolean().required(),
  maxSaleDiscount: Yup.number()
    .nullable()
    .min(0, 'Discount cannot be negative')
    .max(100, 'Discount cannot exceed 100%'),
  class: Yup.string().nullable(),
});

const MedicineForm: React.FC<MedicineFormProps> = ({ medicineId, onSuccess }) => {
  const { t } = useTranslation();
  const isEdit = Boolean(medicineId);
  const { data, isLoading } = useDetailQuery<MedicineFormValues>(
    API_ENDPOINTS.MEDICINES,
    medicineId,
  );

  const { mutate: createMedicine } = useCreateMutation(API_ENDPOINTS.MEDICINES);
  const { mutate: updateMedicine } = useUpdateMutation(API_ENDPOINTS.MEDICINES);

  if (isEdit && isLoading) return <Spinner color="primary" />;

  return (
    <Formik
      enableReinitialize
      initialValues={{
        id: data?.id || '',
        name: data?.name || '',
        alias: data?.alias || '',
        genericName: data?.genericName || null,
        description: data?.description || '',
        dosageForm: data?.dosageForm || '',
        strength: data?.strength || '',
        routeOfAdministration: data?.routeOfAdministration || '',
        medicineType: data?.medicineType || '',
        prescriptionRequired: data?.prescriptionRequired ?? false,
        storageConditions: data?.storageConditions || '',
        shelfLifeMonths: data?.shelfLifeMonths ?? null,
        approvalStatus: data?.approvalStatus || ApprovalStatus.Pending,
        approvalDate: data?.approvalDate || null,
        isActive: data?.isActive ?? true,
        itemAlert: data?.itemAlert ?? false,
        allowDue: data?.allowDue ?? false,
        printable: data?.printable ?? false,
        generatePO: data?.generatePO ?? false,
        maxSaleDiscount: data?.maxSaleDiscount ?? null,
        class: data?.class || null,
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        if (isEdit && medicineId) {
          updateMedicine({ id: medicineId, data: values }, { onSuccess });
        } else {
          createMedicine(values, { onSuccess });
        }
      }}
    >
      {({ values, handleChange, setFieldValue, errors, touched }) => (
        <Form>
          {/* Text and select fields */}

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label htmlFor="name">{t('Name')}</Label>
                <Input
                  id="name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  invalid={!!(errors.name && touched.name)}
                />
                <ErrorMessage name="name" component={FormText} className="text-danger" />
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label htmlFor="alias">{t('Alias')}</Label>
                <Input
                  id="alias"
                  name="alias"
                  value={values.alias}
                  onChange={handleChange}
                  invalid={!!(errors.alias && touched.alias)}
                />
                <ErrorMessage name="alias" component={FormText} className="text-danger" />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label htmlFor="genericName">{t('Generic Name')}</Label>
                <Input
                  id="genericName"
                  name="genericName"
                  value={values.genericName || ''}
                  onChange={handleChange}
                  invalid={!!(errors.genericName && touched.genericName)}
                />
                <ErrorMessage name="genericName" component={FormText} className="text-danger" />
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label htmlFor="dosageForm">{t('Dosage Form')}</Label>
                <Input
                  id="dosageForm"
                  name="dosageForm"
                  value={values.dosageForm}
                  onChange={handleChange}
                  invalid={!!(errors.dosageForm && touched.dosageForm)}
                />
                <ErrorMessage name="dosageForm" component={FormText} className="text-danger" />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label htmlFor="description">{t('Description')}</Label>
                <Input
                  id="description"
                  name="description"
                  type="textarea"
                  value={values.description}
                  onChange={handleChange}
                  invalid={!!(errors.description && touched.description)}
                />
                <ErrorMessage name="description" component={FormText} className="text-danger" />
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label htmlFor="strength">{t('Strength')}</Label>
                <Input
                  id="strength"
                  name="strength"
                  value={values.strength}
                  onChange={handleChange}
                  invalid={!!(errors.strength && touched.strength)}
                />
                <ErrorMessage name="strength" component={FormText} className="text-danger" />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label htmlFor="routeOfAdministration">{t('Route of Administration')}</Label>
                <Input
                  id="routeOfAdministration"
                  name="routeOfAdministration"
                  value={values.routeOfAdministration}
                  onChange={handleChange}
                  invalid={!!(errors.routeOfAdministration && touched.routeOfAdministration)}
                />
                <ErrorMessage
                  name="routeOfAdministration"
                  component={FormText}
                  className="text-danger"
                />
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label>{t('Medicine Type')}</Label>
                <Select
                  options={Object.values(MedicineType).map((type) => ({
                    label: t(type),
                    value: type,
                  }))}
                  value={
                    values.medicineType
                      ? {
                          label: t(values.medicineType),
                          value: values.medicineType,
                        }
                      : null
                  }
                  onChange={(opt) => setFieldValue('medicineType', opt?.value || '')}
                  styles={customSelectStyles}
                  placeholder={t('Select Medicine Type')}
                />
                {errors.medicineType && touched.medicineType && (
                  <FormText color="danger">{errors.medicineType}</FormText>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label htmlFor="storageConditions">{t('Storage Conditions')}</Label>
                <Input
                  id="storageConditions"
                  name="storageConditions"
                  value={values.storageConditions}
                  onChange={handleChange}
                  invalid={!!(errors.storageConditions && touched.storageConditions)}
                />
                <ErrorMessage
                  name="storageConditions"
                  component={FormText}
                  className="text-danger"
                />
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label htmlFor="shelfLifeMonths">{t('Shelf Life (Months)')}</Label>
                <Input
                  type="number"
                  id="shelfLifeMonths"
                  name="shelfLifeMonths"
                  value={values.shelfLifeMonths ?? ''}
                  onChange={handleChange}
                  invalid={!!(errors.shelfLifeMonths && touched.shelfLifeMonths)}
                  min={0}
                />
                <ErrorMessage name="shelfLifeMonths" component={FormText} className="text-danger" />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>{t('Approval Status')}</Label>
                <Select
                  options={Object.values(ApprovalStatus).map((status) => ({
                    label: t(status),
                    value: status,
                  }))}
                  value={
                    values.approvalStatus
                      ? {
                          label: t(values.approvalStatus),
                          value: values.approvalStatus,
                        }
                      : null
                  }
                  onChange={(opt) => setFieldValue('approvalStatus', opt?.value || '')}
                  styles={customSelectStyles}
                  placeholder={t('Select Approval Status')}
                />
                {errors.approvalStatus && touched.approvalStatus && (
                  <FormText color="danger">{errors.approvalStatus}</FormText>
                )}
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label htmlFor="approvalDate">{t('Approval Date')}</Label>
                <Input
                  type="datetime-local"
                  id="approvalDate"
                  name="approvalDate"
                  value={values.approvalDate || ''}
                  onChange={handleChange}
                  invalid={!!(errors.approvalDate && touched.approvalDate)}
                />
                <ErrorMessage name="approvalDate" component={FormText} className="text-danger" />
              </FormGroup>
            </Col>
          </Row>

          {/* Additional text/number fields before checkboxes */}
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label htmlFor="maxSaleDiscount">{t('Max Sale Discount (%)')}</Label>
                <Input
                  type="number"
                  id="maxSaleDiscount"
                  name="maxSaleDiscount"
                  value={values.maxSaleDiscount ?? ''}
                  onChange={handleChange}
                  invalid={!!(errors.maxSaleDiscount && touched.maxSaleDiscount)}
                  min={0}
                  max={100}
                />
                <ErrorMessage name="maxSaleDiscount" component={FormText} className="text-danger" />
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label htmlFor="class">{t('Class')}</Label>
                <Input
                  id="class"
                  name="class"
                  value={values.class || ''}
                  onChange={handleChange}
                  invalid={!!(errors.class && touched.class)}
                />
                <ErrorMessage name="class" component={FormText} className="text-danger" />
              </FormGroup>
            </Col>
          </Row>

          {/* CHECKBOXES GROUPED AT BOTTOM */}
          <Row className="mt-3">
            <Col md={4}>
              <FormGroup check>
                <Input
                  type="checkbox"
                  id="prescriptionRequired"
                  name="prescriptionRequired"
                  checked={values.prescriptionRequired}
                  onChange={handleChange}
                />
                <Label check htmlFor="prescriptionRequired">
                  {t('Prescription Required')}
                </Label>
                <ErrorMessage
                  name="prescriptionRequired"
                  component={FormText}
                  className="text-danger"
                />
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup check>
                <Input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={values.isActive}
                  onChange={handleChange}
                />
                <Label check htmlFor="isActive">
                  {t('Is Active')}
                </Label>
                <ErrorMessage name="isActive" component={FormText} className="text-danger" />
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup check>
                <Input
                  type="checkbox"
                  id="itemAlert"
                  name="itemAlert"
                  checked={values.itemAlert}
                  onChange={handleChange}
                />
                <Label check htmlFor="itemAlert">
                  {t('Item Alert')}
                </Label>
                <ErrorMessage name="itemAlert" component={FormText} className="text-danger" />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <FormGroup check>
                <Input
                  type="checkbox"
                  id="allowDue"
                  name="allowDue"
                  checked={values.allowDue}
                  onChange={handleChange}
                />
                <Label check htmlFor="allowDue">
                  {t('Allow Due')}
                </Label>
                <ErrorMessage name="allowDue" component={FormText} className="text-danger" />
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup check>
                <Input
                  type="checkbox"
                  id="printable"
                  name="printable"
                  checked={values.printable}
                  onChange={handleChange}
                />
                <Label check htmlFor="printable">
                  {t('Printable')}
                </Label>
                <ErrorMessage name="printable" component={FormText} className="text-danger" />
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup check>
                <Input
                  type="checkbox"
                  id="generatePO"
                  name="generatePO"
                  checked={values.generatePO}
                  onChange={handleChange}
                />
                <Label check htmlFor="generatePO">
                  {t('Generate PO')}
                </Label>
                <ErrorMessage name="generatePO" component={FormText} className="text-danger" />
              </FormGroup>
            </Col>
          </Row>

          <div className="d-flex justify-content-end mt-3">
            <Button type="submit" color="primary">
              {isEdit ? t('Update') : t('Save')}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default MedicineForm;
